import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useSlugNavigate } from '../hooks/useNavigation'
import { listMigrations } from '../api/migrations'
import DashboardLayout from '../components/DashboardLayout'

export default function DashboardPage() {
  const { user } = useAuth()
  const navigate = useSlugNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [allMigrations, setAllMigrations] = useState([])
  const [loading, setLoading] = useState(true)

  // Fetch migrations when tenancy changes
  useEffect(() => {
    const fetchMigrations = async () => {
      if (!user?.active_tenancy_id) return

      setLoading(true)
      try {
        const response = await listMigrations(user.active_tenancy_id)
        // API returns { migrations: [...], total: int }
        const migrationsArray = response.migrations || []
        // Sort by updated_at descending
        const sorted = migrationsArray.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
        setAllMigrations(sorted)
      } catch (error) {
        console.error('Failed to fetch migrations:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchMigrations()
  }, [user?.active_tenancy_id])

  // Get latest 5 for display
  const migrations = allMigrations.slice(0, 5)

  // Calculate stats from ALL migrations (uppercase status values from API)
  const stats = [
    {
      label: 'Total Migrations',
      value: allMigrations.length.toString(),
      highlight: true
    },
    {
      label: 'Completed',
      value: allMigrations.filter(m => m.status === 'COMPLETED').length.toString()
    },
    {
      label: 'In Progress',
      value: allMigrations.filter(m => ['IN_PROGRESS', 'CONFIGURED', 'READY'].includes(m.status)).length.toString()
    },
    {
      label: 'Draft',
      value: allMigrations.filter(m => m.status === 'DRAFT').length.toString()
    },
  ]

  const getStatusColor = (status) => {
    const colors = {
      DRAFT: 'gray',
      CONFIGURED: 'blue',
      READY: 'blue',
      IN_PROGRESS: 'amber',
      COMPLETED: 'emerald',
      FAILED: 'red',
      ARCHIVED: 'gray'
    }
    return colors[status] || 'gray'
  }

  const getStatusBadgeClasses = (color) => {
    const colors = {
      amber: 'bg-amber-100 text-amber-700',
      emerald: 'bg-emerald-100 text-emerald-700',
      blue: 'bg-blue-100 text-blue-700',
      gray: 'bg-gray-100 text-gray-700',
      red: 'bg-red-100 text-red-700',
    }
    return colors[color] || colors.gray
  }

  const getStatusDotClasses = (color) => {
    const colors = {
      amber: 'bg-amber-500',
      emerald: 'bg-emerald-500',
      blue: 'bg-blue-500',
      gray: 'bg-gray-500',
      red: 'bg-red-500',
    }
    return colors[color] || colors.gray
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatStatus = (status) => {
    return status.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ')
  }

  return (
    <DashboardLayout>
      {/* Top Navigation Bar */}
      <header className="flex items-center justify-between whitespace-nowrap border-b border-solid border-[#dbe0e6] bg-white px-8 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-4 text-[#111418]">
          <h2 className="text-lg font-bold leading-tight tracking-[-0.015em]">Dashboard Overview</h2>
        </div>
        <div className="flex flex-1 justify-end gap-4 items-center">
          <label className="flex flex-col min-w-40 h-10 max-w-64">
            <div className="flex w-full flex-1 items-stretch rounded-lg h-full border border-[#dbe0e6]">
              <div className="text-[#617589] flex bg-[#f0f2f4] items-center justify-center pl-4 rounded-l-lg border-r-0">
                <span className="material-symbols-outlined text-[20px]">search</span>
              </div>
              <input
                className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-[#111418] focus:outline-0 focus:ring-0 border-none bg-[#f0f2f4] h-full placeholder:text-[#617589] px-4 rounded-l-none border-l-0 pl-2 text-sm font-normal"
                placeholder="Search migrations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </label>
          <button className="flex cursor-pointer items-center justify-center rounded-lg h-10 w-10 bg-[#f0f2f4] text-[#111418]">
            <span className="material-symbols-outlined text-[20px]">notifications</span>
          </button>
        </div>
      </header>

      {/* Page Content */}
      <div className="p-8 max-w-7xl mx-auto w-full">
        {/* Page Heading */}
        <div className="flex flex-wrap justify-between items-end gap-3 mb-8">
          <div className="flex min-w-72 flex-col gap-2">
            <p className="text-[#111418] text-3xl font-black leading-tight tracking-[-0.033em]">
              Welcome back{user?.first_name ? `, ${user.first_name}` : ', Partner'}
            </p>
            <p className="text-[#617589] text-base font-normal leading-normal">Manage your accounting migrations and track credits.</p>
          </div>
          <button
            onClick={() => navigate('/migrations')}
            className="flex min-w-[140px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-6 bg-primary text-white text-sm font-bold leading-normal tracking-[0.015em] hover:bg-primary/90 transition-all shadow-md"
          >
            <span className="material-symbols-outlined mr-2">add_circle</span>
            <span className="truncate">New Migration</span>
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`flex flex-col gap-2 rounded-xl p-6 bg-white border border-[#dbe0e6] shadow-sm ${
                stat.highlight ? 'border-l-4 border-l-primary' : ''
              }`}
            >
              <p className="text-[#617589] text-sm font-medium leading-normal uppercase tracking-wider">{stat.label}</p>
              <div className="flex items-baseline gap-2">
                <p className="text-[#111418] tracking-light text-3xl font-bold leading-tight">{stat.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Latest Migrations Section */}
        <div className="bg-white rounded-xl border border-[#dbe0e6] shadow-sm">
          <div className="flex items-center justify-between px-6 py-5 border-b border-[#dbe0e6]">
            <h2 className="text-[#111418] text-lg font-bold leading-tight">Latest Migrations</h2>
            <button
              onClick={() => navigate('/migrations')}
              className="text-sm font-bold text-primary hover:underline"
            >
              View all
            </button>
          </div>
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : migrations.length === 0 ? (
              <div className="text-center py-12">
                <span className="material-symbols-outlined text-4xl text-[#617589] mb-2">sync_alt</span>
                <p className="text-[#617589] text-sm">No migrations yet</p>
                <button
                  onClick={() => navigate('/migrations')}
                  className="mt-4 text-sm font-bold text-primary hover:underline"
                >
                  Create your first migration
                </button>
              </div>
            ) : (
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-[#f8fafc]">
                    <th className="px-6 py-3 text-[#617589] text-xs font-bold uppercase tracking-wider">Migration Name</th>
                    <th className="px-6 py-3 text-[#617589] text-xs font-bold uppercase tracking-wider">Source System</th>
                    <th className="px-6 py-3 text-[#617589] text-xs font-bold uppercase tracking-wider">Last Updated</th>
                    <th className="px-6 py-3 text-[#617589] text-xs font-bold uppercase tracking-wider text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#dbe0e6]">
                  {migrations.map((migration) => {
                    const statusColor = getStatusColor(migration.status)
                    return (
                      <tr
                        key={migration.id}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/migrations/${migration.id}`)}
                      >
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-[#111418] font-bold text-sm">{migration.name}</span>
                            {migration.description && (
                              <span className="text-[#617589] text-xs truncate max-w-xs">{migration.description}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#111418]">
                          {migration.source_system || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-[#111418]">{formatDate(migration.updated_at)}</td>
                        <td className="px-6 py-4 text-right">
                          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${getStatusBadgeClasses(statusColor)}`}>
                            <span className={`size-1.5 rounded-full ${getStatusDotClasses(statusColor)}`}></span>
                            {formatStatus(migration.status)}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
